import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apps } from "@/lib/apps";

const BASE = process.env.REACT_APP_BACKEND_URL;
const MAX = 12;

const IconBtn = ({ onClick, disabled, label, children, testid, danger }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testid} className={`grid h-8 w-8 place-items-center rounded-full border border-white/15 bg-[#05070f]/80 transition-colors disabled:opacity-30 ${danger ? "hover:bg-rose-500 hover:text-white" : "hover:bg-white hover:text-[#05070f]"}`}>
    {children}
  </button>
);

const AppShots = ({ app, adminKey }) => {
  const [shots, setShots] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const headers = { "X-Admin-Key": adminKey };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/apps/${app.slug}/screenshots`);
      setShots(Array.isArray(data) ? data : []);
    } catch {
      setShots([]);
    }
  }, [app.slug]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        await axios.post(`${BASE}/api/admin/apps/${app.slug}/screenshots`, form, { headers });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (ok) toast.success(`${ok} screenshot(s) added to ${app.name}.`);
    if (input.current) input.current.value = "";
    await load();
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this screenshot from the website?")) return;
    try {
      await axios.delete(`${BASE}/api/admin/screenshots/${id}`, { headers });
      await load();
    } catch {
      toast.error("Could not remove screenshot.");
    }
  };

  const move = async (i, dir) => {
    const ids = shots.map((s) => s.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try {
      const { data } = await axios.put(`${BASE}/api/admin/apps/${app.slug}/screenshots/order`, { ids }, { headers });
      setShots(Array.isArray(data) ? data : shots);
    } catch {
      toast.error("Could not reorder screenshots.");
    }
  };

  return (
    <div data-testid={`admin-shots-${app.slug}`} className="glass rounded-3xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={app.logo} alt="" className="h-12 w-12 rounded-2xl object-cover" style={{ transform: `scale(${app.logoScale})` }} />
          <div>
            <p className="font-display text-xl font-semibold">{app.name}</p>
            <p data-testid={`admin-shots-count-${app.slug}`} className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">{shots ? shots.length : "…"} / {MAX} screenshots</p>
          </div>
        </div>
        <label className={`btn-primary !py-2.5 ${busy || (shots && shots.length >= MAX) ? "pointer-events-none opacity-40" : ""}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Upload screenshots
          <input ref={input} type="file" multiple accept="image/png,image/jpeg,image/webp" className="sr-only" data-testid={`admin-shots-input-${app.slug}`} onChange={(e) => e.target.files?.length && upload([...e.target.files])} />
        </label>
      </div>
      {shots && shots.length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {shots.map((s, i) => (
            <div key={s.id} data-testid={`admin-shot-${app.slug}-${i}`} className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black">
              <img src={`${BASE}${s.url}`} alt={`${app.name} screenshot ${i + 1}`} className="aspect-[9/19.5] w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1.5 bg-gradient-to-t from-black/80 to-transparent p-2 pt-8">
                <IconBtn onClick={() => move(i, -1)} disabled={i === 0} label="Move left" testid={`admin-shot-left-${app.slug}-${i}`}><ArrowLeft className="h-3.5 w-3.5" /></IconBtn>
                <IconBtn onClick={() => move(i, 1)} disabled={i === shots.length - 1} label="Move right" testid={`admin-shot-right-${app.slug}-${i}`}><ArrowRight className="h-3.5 w-3.5" /></IconBtn>
                <IconBtn onClick={() => remove(s.id)} label="Delete" danger testid={`admin-shot-delete-${app.slug}-${i}`}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminScreenshots = ({ adminKey }) => (
  <div data-testid="admin-screenshots" className="space-y-4">
    <p className="max-w-2xl text-sm text-white/55">
      Upload phone screenshots (PNG, JPG or WebP, max 8 MB, up to {MAX} per app). They appear on the app's page in this order — use the arrows to rearrange. Tall portrait screenshots look best.
    </p>
    {apps.map((app) => <AppShots key={app.slug} app={app} adminKey={adminKey} />)}
  </div>
);
